import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { TDictObject } from '../../shared/IDirectoriesService';
import type { ContextOption } from '../components/ContextSelectionPanel/BaseContextSelectionPanel';

import type { RecommendationContextDirectory } from './components/RecommendationsContextPanel';
import type { RecommendationsService } from './services/RecommendationsService';

function toOptions(list: TDictObject[], nameKey = 'name'): ContextOption[] {
  return list.map((obj) => ({
    label: (obj.data[nameKey]?.toString() ?? '').trim() || String(obj.id),
    value: typeof obj.id === 'number' ? obj.id : Number(obj.id) || 0,
  }));
}

export function useRecommendationContextOptions(service: RecommendationsService): RecommendationContextDirectory[] {
  const { t } = useTranslation();
  const {
    workClassifierStageTypes,
    wellboreTypes,
    trajectoryTypes,
    sectionTypes,
    stratUnitTypes,
    reservoirTypes,
    runTypes,
    operationTypes,
  } = service;
  return useMemo(
    () => [
      {
        id: 'stage',
        title: 'alertsRecommendations:contextTitle.stage',
        options: toOptions(workClassifierStageTypes ?? []),
        multi: true,
        fieldName: 'stageIds',
      },
      {
        id: 'wellboreType',
        title: 'alertsRecommendations:contextTitle.wellboreType',
        options: toOptions(wellboreTypes ?? [], 'customName'),
        multi: true,
        fieldName: 'wellboreTypeIds',
      },
      {
        id: 'trajectory',
        title: 'alertsRecommendations:contextTitle.trajectoryType',
        options: toOptions(trajectoryTypes ?? []),
        multi: true,
        fieldName: 'trajectoryTypeIds',
      },
      {
        id: 'section',
        title: 'alertsRecommendations:contextTitle.section',
        options: toOptions(sectionTypes ?? []),
        multi: true,
        fieldName: 'sectionTypeIds',
      },
      {
        id: 'holeTypes',
        title: 'alertsRecommendations:contextTitle.holeType',
        options: [
          { value: 'cased', label: t('alertsRecommendations:holeType.cased') },
          { value: 'open', label: t('alertsRecommendations:holeType.open') },
        ],
        multi: true,
        fieldName: 'holeTypes',
        valueType: 'string' as const,
      },
      {
        id: 'stratUnit',
        title: 'alertsRecommendations:contextTitle.stratUnit',
        options: toOptions(stratUnitTypes ?? []),
        multi: true,
        fieldName: 'stratUnitTypeIds',
      },
      {
        id: 'reservoir',
        title: 'alertsRecommendations:contextTitle.reservoir',
        options: toOptions(reservoirTypes ?? []),
        multi: true,
        fieldName: 'reservoirIds',
      },
      {
        id: 'run',
        title: 'alertsRecommendations:contextTitle.run',
        options: toOptions(runTypes ?? []),
        multi: true,
        fieldName: 'runTypeIds',
      },
      {
        id: 'operation',
        title: 'alertsRecommendations:contextTitle.operation',
        options: toOptions(operationTypes ?? []),
        multi: true,
        fieldName: 'operationIds',
      },
    ],
    [
      operationTypes,
      reservoirTypes,
      runTypes,
      sectionTypes,
      stratUnitTypes,
      t,
      trajectoryTypes,
      wellboreTypes,
      workClassifierStageTypes,
    ],
  );
}
